import { Lock, ArrowRight, Sparkles } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useProducts, getBundles, formatPrice } from '@/hooks/use-products';
import type { EventFeatures } from '@/hooks/use-event-limits';

const FEATURE_LABELS: Record<keyof EventFeatures, string> = {
  analytics: 'Statistici Avansate',
  videoUploads: 'Upload Video',
  moderationQueue: 'Moderare Conținut',
  uploadPortal: 'Portal Upload Invitați',
  liveWall: 'Live Wall',
  mediaAlbum: 'Album Foto Digital',
};

const FEATURE_DESCRIPTIONS: Record<keyof EventFeatures, string> = {
  analytics: 'Statistici detaliate despre vizualizări, confirmări și engagement.',
  videoUploads: 'Permite invitaților să încarce videoclipuri în albumul foto.',
  moderationQueue: 'Aprobă sau respinge conținutul încărcat de invitați înainte de publicare.',
  uploadPortal: 'Portal QR unde invitații pot încărca fotografii și videoclipuri.',
  liveWall: 'Perete live cu fotografiile și mesajele invitaților în timp real.',
  mediaAlbum: 'Album foto digital pentru colectarea și vizualizarea amintirilor din eveniment.',
};

interface FeatureLockedDialogProps {
  feature: keyof EventFeatures | null;
  eventId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function FeatureLockedDialog({ feature, eventId, open, onOpenChange }: FeatureLockedDialogProps) {
  const navigate = useNavigate();
  const { data: products } = useProducts();

  if (!feature) return null;

  // Filter bundles that include this feature
  const bundles = products ? getBundles(products) : [];
  const matchingBundles = bundles
    .filter((p) => p.attributes[feature as keyof typeof p.attributes] === true)
    .sort((a, b) => a.price - b.price);

  const featureName = FEATURE_LABELS[feature];
  const featureDescription = FEATURE_DESCRIPTIONS[feature];

  const handleBuy = (productKey: string) => {
    onOpenChange(false);
    navigate(`/checkout/new?package=${productKey}&eventId=${eventId}`);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader className="text-center items-center">
          <div className="w-14 h-14 rounded-2xl bg-amber-100 flex items-center justify-center mx-auto mb-2">
            <Lock className="w-6 h-6 text-amber-600" />
          </div>
          <DialogTitle className="font-serif text-xl">{featureName}</DialogTitle>
          <DialogDescription className="text-sm leading-relaxed">
            {featureDescription}
            <span className="block mt-2 font-medium text-foreground/80">
              Această funcționalitate nu este inclusă în abonamentul tău actual. Alege un pachet care o include:
            </span>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 mt-2">
          {matchingBundles.map((product, i) => (
            <div
              key={product.id}
              className={`relative rounded-xl border p-4 transition-all ${
                i === matchingBundles.length - 1 && matchingBundles.length > 1
                  ? 'border-primary/40 bg-primary/[0.03] ring-1 ring-primary/20'
                  : 'border-border/60 bg-background'
              }`}
            >
              {i === matchingBundles.length - 1 && matchingBundles.length > 1 && (
                <span className="absolute -top-2.5 right-3 inline-flex items-center gap-1 bg-primary text-white text-[10px] font-bold px-2.5 py-0.5 rounded-full tracking-wide uppercase">
                  <Sparkles className="w-3 h-3" />
                  Recomandat
                </span>
              )}
              <div className="flex items-center justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm text-foreground truncate">{product.displayName}</p>
                  {product.description ? (
                    <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">{product.description}</p>
                  ) : null}
                </div>
                <div className="text-right shrink-0">
                  <p className="text-lg font-bold text-foreground">
                    {formatPrice(product.price, product.currency)}
                  </p>
                </div>
              </div>
              <Button
                size="sm"
                variant={i === matchingBundles.length - 1 && matchingBundles.length > 1 ? 'default' : 'outline'}
                className="w-full mt-3 gap-2"
                onClick={() => handleBuy(product.productKey)}
              >
                Cumpără
                <ArrowRight className="w-3.5 h-3.5" />
              </Button>
            </div>
          ))}

          {matchingBundles.length === 0 && (
            <p className="text-sm text-muted-foreground text-center py-4">
              Nu există pachete disponibile pentru această funcționalitate.
            </p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
